import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { UxserviceService } from './uxservice.service';

export interface CartItem {
    equipmentId: string;
    equipmentData: any;
    startDate: Date | string;
    endDate: Date | string;
    priceType: 'daily' | 'weekly' | 'monthly' | 'per_trip' | 'cbm' | 'hourly';
    rate: number; // rate per unit
    totalAmount: number; // total for this item
    daysCount: number; // or unit count
    vendorId?: string; // crucial for notification
    address?: string; // ✅ ADDED
    id?: string; // Unique identifier for each cart item instance
}

@Injectable({
    providedIn: 'root'
})
export class CartService {
    private cartItemsParam = new BehaviorSubject<CartItem[]>([]);
    public cartItems$ = this.cartItemsParam.asObservable();

    private itemAddedSubject = new Subject<void>();
    public itemAdded$ = this.itemAddedSubject.asObservable();

    constructor(
        private auth: Auth,
        private firestore: Firestore,
        private ux: UxserviceService
    ) {
        this.loadCart();
        this.listenForAuthChanges();
    }

    private listenForAuthChanges() {
        onAuthStateChanged(this.auth, (user) => {
            if (user) {
                this.syncCartWithFirestore(user.uid);
            } else {
                // On logout, clear local cart to prevent item leakage between users
                this.cartItemsParam.next([]);
                sessionStorage.removeItem('alyn360_cart');
                console.log('Cart cleared locally on logout');
            }
        });
    }

    /**
     * Merges current local cart with the cloud cart when a user logs in.
     */
    private async syncCartWithFirestore(uid: string) {
        try {
            const cartRef = doc(this.firestore, `carts/${uid}`);
            const cartSnap = await getDoc(cartRef);
            let cloudItems: CartItem[] = [];

            if (cartSnap.exists()) {
                cloudItems = cartSnap.data()['items'] || [];
            }

            const localItems = this.cartItemsParam.value;
            const merged = [...cloudItems];

            const normalize = (d: Date | string) => (d instanceof Date ? d.toISOString() : d);

            // Merge local guest items into the cloud items if they don't already exist
            localItems.forEach(local => {
                const isDuplicate = merged.some(cloud =>
                    cloud.equipmentId === local.equipmentId &&
                    normalize(cloud.startDate) === normalize(local.startDate) &&
                    normalize(cloud.endDate) === normalize(local.endDate)
                );
                if (!isDuplicate) {
                    merged.push(local);
                }
            });

            // Update local state and cloud
            this.cartItemsParam.next(merged);
            this.saveCartLocally(merged);
            await this.saveToFirestore(merged, uid);
            console.log('Cart sync complete for user:', uid);
        } catch (error) {
            console.error('Error syncing cart with Firestore:', error);
        }
    }

    private async saveToFirestore(items: CartItem[], uid?: string) {
        const userId = uid || this.auth.currentUser?.uid;
        if (!userId) return;

        try {
            const cartRef = doc(this.firestore, `carts/${userId}`);
            await setDoc(cartRef, {
                items,
                updatedAt: new Date().toISOString()
            }, { merge: true });
        } catch (e) {
            console.error('Failed to save cart to Firestore', e);
        }
    }

    get cartItems(): CartItem[] {
        return this.cartItemsParam.value;
    }

    addToCart(item: CartItem): boolean {
        const current = this.cartItems;

        // Check for duplicates
        const normalize = (d: Date | string) => (d instanceof Date ? d.toISOString() : d);

        const isDuplicate = current.some(i =>
            i.equipmentId === item.equipmentId &&
            normalize(i.startDate) === normalize(item.startDate) &&
            normalize(i.endDate) === normalize(item.endDate)
        );

        if (isDuplicate) {
            this.ux.showError('This vehicle is already in your cart for the selected dates.');
            return false;
        }

        // Generate instance ID for precise removal
        const newItem = {
            ...item,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 5)
        };

        const updated = [...current, newItem];
        this.cartItemsParam.next(updated);
        this.saveCart(updated);
        this.itemAddedSubject.next();
        return true;
    }

    removeFromCart(instanceId: string | undefined) {
        if (!instanceId) return;
        const current = this.cartItems;
        const updated = current.filter(i => i.id !== instanceId);
        this.cartItemsParam.next(updated);
        this.saveCart(updated);
    }

    updateCartItem(instanceId: string, updatedItem: CartItem): boolean {
        const current = this.cartItems;
        const index = current.findIndex(i => i.id === instanceId);

        if (index > -1) {
            const updated = [...current];
            updated[index] = {
                ...updatedItem,
                id: instanceId // Preserve the same instance ID
            };
            this.cartItemsParam.next(updated);
            this.saveCart(updated);
            return true;
        }
        return false;
    }

    getCartItem(id: string): CartItem | undefined {
        return this.cartItems.find(i => i.id === id);
    }

    clearCart() {
        this.cartItemsParam.next([]);
        this.saveCart([]);
    }

    getCartCount() {
        return this.cartItems$.pipe(map(items => items.length));
    }

    calculateTotal(): number {
        return this.cartItems.reduce((sum, item) => sum + item.totalAmount, 0);
    }

    private saveCartLocally(items: CartItem[]) {
        sessionStorage.setItem('alyn360_cart', JSON.stringify(items));
    }

    private saveCart(items: CartItem[]) {
        this.saveCartLocally(items);
        if (this.auth.currentUser) {
            this.saveToFirestore(items);
        }
    }

    private loadCart() {
        const saved = sessionStorage.getItem('alyn360_cart');
        if (saved) {
            try {
                this.cartItemsParam.next(JSON.parse(saved));
            } catch (e) {
                console.error('Failed to parse cart', e);
                this.cartItemsParam.next([]);
            }
        }
    }
}
